import NetworkUtil from "./NetworkUtil";
import {Sample} from "../types/Sample";
import {ServerResponse} from "../types/ServerResponse";
import {ReportDownloadHistory, ReportDownloadSummary} from "../types/ReportDownload";

export default class SamplesAPI {

    public static async getFilteredSamples(page, filter):Promise<{num_pages:number, totalSamples:number, table_data?:Sample[]}>{
        return NetworkUtil.makePost("/api/admin/sampleSearch?page=" + page, filter);
    }

    public static async getFilteredSamplesForCheckin(page, filter):Promise<{num_pages:number, totalSamples:number, table_data?:Sample[]}>{
        return NetworkUtil.makePost("/api/admin/sampleSearchForCheckin?page=" + page, filter);
    }

    public static async getAllSamplesForAdminHome():Promise<{facilitySamplesArray}>{
        return NetworkUtil.makeGet("/api/admin/getAllSamplesForAdminHome")
    }

    public static async getTodaySamples():Promise<{num_pages:number, totalSamples:number, table_data?:Sample[]}>{
        return NetworkUtil.makeGet("/api/admin/getTodaySamples")
    }

    public static async resultRetrievalForSample(reqNum):Promise<any>{
        return NetworkUtil.makePost("/api/admin/resultRetrievalForSample" , {reqNum: reqNum})
    }

    public static async updateSample(sample, insuranceInformation, insuranceCards, submissionFiles?):Promise<ServerResponse>{
        let data = new FormData()
        data.set('insuranceInformation', JSON.stringify(insuranceInformation))
        data.set('patientInformation', JSON.stringify(sample))
        if(insuranceCards){
            for(let i = 0; i < insuranceCards.length; i++){
                let ext = insuranceCards[i].type.split('/').pop();
                data.append('insuranceCards', insuranceCards[i], `${i === 0 ? 'front' : 'back'}.${ext}`)
            }
        }
        if(submissionFiles){
            for(let i = 0; i < submissionFiles.length; i++){
                data.append('submissionFiles', submissionFiles[i])
            }
        }
        return NetworkUtil.postForm("/api/admin/sampleEdit", data);
    }

    public static async updateResultsAccessedDate(id):Promise<ServerResponse>{
        return NetworkUtil.makePost("/api/admin/sampleResultsAccessedDate", {ID: id});
    }

    public static editSample(editBody:SampleCheckinBody):Promise<ServerResponse>{
        return NetworkUtil.makePost("/api/admin/sample/checkin", editBody);
    }

    public static getInsuranceCardImagesFromCloud(data):Promise<any>{
        return NetworkUtil.makePostForBlob("/api/admin/getInsuranceCardImagesFromCloud", data);
    }

    public static downloadFilesFromCloud(data):Promise<any>{
        return NetworkUtil.makePostForBlob("/api/admin/downloadFilesFromCloud", data);
    }

    public static getSubmittedFilesFromCloud(reqNum):Promise<{success: boolean, reason?:string,  data?}>{
        return NetworkUtil.makePost("/api/admin/getSubmittedFilesFromCloud", {ReqNum: reqNum});
    }

    public static async resultsReviewedSendPatientEmail(id):Promise<ServerResponse>{
        return NetworkUtil.makePost("/api/admin/resultsReviewedSendPatientEmail", {ID: id});
    }

    public static async generateExcelDocBasedOnService(serviceID, facilityID):Promise<any>{
        return NetworkUtil.makePostForBlob("/api/admin/generateExcelDocBasedOnService", {serviceID: serviceID, facilityID: facilityID});
    }
    // returns array of filePath's for versions of report stored in the cloud
    public static getResultReportListForReqNum(reqNum:string):Promise<{success:boolean,reason:string,data:[]}>{
        return NetworkUtil.makePost("/api/admin/pdfReport/getResultReportListForReqNum",{reqNum:reqNum});
    }

    // returns filePath of result report that is generated and stored in the cloud
    public static generateResultReportForReqNum(reqNum:string):Promise<{success:boolean,reason:string,data}>{
        return NetworkUtil.makePost("/api/admin/pdfReport/generateResultReportForReqNum", {reqNum:reqNum})
    }

    public static viewResultReport(path:string):Promise<Blob | ServerResponse>{
        return NetworkUtil.makePostForBuffer("/api/admin/pdfReport/viewResultReport",{path:path});
    }

    // returns array of filePath's for versions of report stored in the cloud
    public static getRequisitionFormListForReqNum(reqNum:string):Promise<{success:boolean,reason:string,data:[]}>{
        return NetworkUtil.makePost("/api/admin/pdfReport/getRequisitionFormListForReqNum",{reqNum:reqNum});
    }

    // returns filePath of result report that is generated and stored in the cloud
    public static generateRequisitionFormForReqNum(reqNum:string):Promise<{success:boolean,reason:string,data}>{
        return NetworkUtil.makePost("/api/admin/pdfReport/generateRequisitionFormForReqNum", {reqNum:reqNum})
    }

    public static viewRequisitionForm(path:string):Promise<Blob | ServerResponse>{
        return NetworkUtil.makePostForBuffer("/api/admin/pdfReport/viewRequisitionForm",{path:path});
    }

    public static upgradeSample(sample, paymentInfo):Promise<{success:boolean,reason:string,data:[]}>{
      return NetworkUtil.makePost("/api/admin/sample/upgradeSample", {sample:sample, paymentInfo: paymentInfo});
    }

    public static async cancelSample(sample):Promise<ServerResponse>{
        return NetworkUtil.makePost("/api/admin/cancelSample", {sample: sample});
    }

    // Result reports come from STARLIMS; samples without a report are listed in X-Skipped-ReqNums
    public static async downloadResultReportPDFs(records):Promise<{blob?: Blob, skippedReqNums: string[], error?: ServerResponse}>{
        const response = await NetworkUtil.makePostForBufferWithHeaders("/api/admin/pdfReport/downloadMultipleSamplePDFs", {records: records, PDFTypeToDownload: "ResultReport"});
        const skippedHeader = response.headers?.get('X-Skipped-ReqNums');
        return { blob: response.blob, error: response.error, skippedReqNums: skippedHeader ? skippedHeader.split(',').map(r => r.trim()).filter(r => r) : [] };
    }

    public static downloadPDFs(reqNums, PDFTypeToDownload):Promise<Blob | ServerResponse>{
        // PDFTypeToDownload is a string that can be either "ResultReport" or "RequisitionForm"
        return NetworkUtil.makePostForBuffer("/api/admin/pdfReport/downloadMultipleSamplePDFs", {records: reqNums, PDFTypeToDownload: PDFTypeToDownload});
    }

    // Retrieves the STARLIMS final-report PDF for a sample. Resolves to a Blob (the PDF)
    // on success, or a ServerResponse ({success:false, reason}) on any non-2xx.
    public static getStarlimsFinalReport(reqNum:string):Promise<Blob | ServerResponse>{
        return NetworkUtil.makePostForBuffer("/api/admin/pdfReport/getStarlimsFinalReport", {reqNum: reqNum});
    }

    // Download counts + last download date per sample/report type, for the Samples table.
    public static getReportDownloadSummary(reqNums:string[]):Promise<{success:boolean,reason:string,data?:ReportDownloadSummary[]}>{
        return NetworkUtil.makePost("/api/admin/pdfReport/getReportDownloadSummary", {reqNums: reqNums});
    }

    // Who downloaded a sample's reports and when, newest first.
    public static getReportDownloadHistory(reqNum:string):Promise<{success:boolean,reason:string,data?:ReportDownloadHistory[]}>{
        return NetworkUtil.makePost("/api/admin/pdfReport/getReportDownloadHistory", {reqNum: reqNum});
    }

}

export interface SampleCheckinBody{
    ID?
    PatientFirstName:string
    PatientLastName:string,
    PatientDOB
    ServiceID
    TestIDs
    SpecimenSourceID
    SpecimenID?
    Lot?
    HasLabTest?
    SpecimenIDsArray?
    LotsArray?
}